import { authClient } from "lib/auth/authClient";
import { BetterAuthSessionData, BetterAuthUserData } from "./utils";

export type BetterAuthSessionResponse = {
  user: BetterAuthUserData;
  session: BetterAuthSessionData;

  // True when the user's company requires 2FA but they haven't set it up yet.
  twoFactorSetupRequired?: boolean;

  // True for a freshly created better-auth account until the webapp fires the one-time signup
  // conversion pixels and acknowledges it (see CurrentUserConfiguredProviders).
  signupConversionPending?: boolean;
};

/**
 * Query options for the Better Auth session. Wrapped this way so the root-route
 * guard can load it once (via `ensureQueryData`) and reuse it across navigations.
 *
 * Returns null when there's no session (e.g. a Rails session that outlived it),
 * which means the 2FA-setup guard just does nothing for those users.
 */
export const betterAuthSessionQueryOptions = () => ({
  queryKey: ["betterAuthSession"] as const,
  queryFn: async (): Promise<BetterAuthSessionResponse | null> => {
    // If we can't fetch the session (auth-service down, network error), return
    // null instead of throwing so the guard never blocks navigation.
    try {
      const { data } = await authClient.getSession();
      return (data as BetterAuthSessionResponse | null) ?? null;
    } catch {
      return null;
    }
  },
  // Never auto-refetch: a background refetch could redirect the user to 2FA
  // setup mid-task. After changing 2FA, manually invalidate this query to
  // refresh it (see TwoFactorSettings).
  staleTime: Infinity,
});

/**
 * True when the auth-service answers that there's no session. Unlike the query above, a failure
 * throws rather than reading as no session, so a query can retry it and tell it apart.
 */
export async function hasNoBetterAuthSession(): Promise<boolean> {
  const { data, error } = await authClient.getSession();
  if (error) throw new Error(`Could not check the better-auth session (${error.status})`);
  return !data;
}
